import moment from "moment";

export type ParseResult<T> = {
  value?: T;
  error?: boolean;
  userFormatted?: string;
};

export function parseCurrency(code: string): ParseResult<string> {
  if (!isValidCurrency(code)) {
    return { error: true };
  }
  return { value: code };
}

export function parseDate(date: string): ParseResult<Date> {
  const m = moment(date, moment.ISO_8601, true);
  if (!m.isValid()) {
    const fallback = moment(
      date,
      [
        "DD.MM.YYYY",
        "D.M.YYYY",
        "YYYY-MM-DD",
        "DD.MM.YY",
        "D.M.YY",
        "DD/MM/YYYY",
        "D/M/YYYY",
        "YYYY/MM/DD",
        "YYYY.MM.DD",
        "MMM D, YYYY",
        "MMMM D, YYYY",
        "D MMM YYYY",
        "D MMMM YYYY",
      ],
      true,
    );
    if (!fallback.isValid()) {
      return { error: true };
    }
    return {
      value: fallback.toDate(),
      userFormatted: fallback.toDate().toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    };
  }
  return {
    value: m.toDate(),
    userFormatted: m.toDate().toLocaleDateString("de-DE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
  };
}

export function parsePurpose(purpose: string): ParseResult<string> {
  const startsWithPattern = /^[A-Z0-9]{4}\+/;
  const includesKrefOrSvwz = /KREF\+|SVWZ\+/;
  if (startsWithPattern.test(purpose) && includesKrefOrSvwz.test(purpose)) {
    const sepaText = parseSepaText(purpose);
    const texts = [sepaText["EREF"], sepaText["SVWZ"]].filter(Boolean);
    return { value: texts.join(" ") };
  }

  return { value: purpose };
}

function parseSepaText(input: string): Record<string, string> {
  return input
    .split(/(?=[A-Z]{4}\+)/)
    .reduce((acc: Record<string, string>, part) => {
      const [key, value] = part.split("+", 2);
      if (key && value !== undefined) acc[key] = value.trim();
      return acc;
    }, {});
}

export function parseAmount(amount: string): ParseResult<number> {
  if (!amount) {
    return { error: true };
  }

  amount = amount.trim();

  if (!/[0-9]/.test(amount)) {
    return { error: true };
  }

  const allowedChars = /[0-9.,\-\+\s]/g;
  const nonAllowed = amount.replace(allowedChars, "");
  if (nonAllowed.length > 3) {
    return { error: true };
  }

  let cleaned = amount.replace(/[^0-9.,\-\+\s]/g, "");

  // Find the last separator (if any)
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const lastSep = Math.max(lastComma, lastDot);

  if (lastSep !== -1) {
    const decimals = cleaned.length - lastSep - 1;

    if (decimals === 3) {
      // Likely thousands separator (e.g. "1.000" => 1000)
      cleaned = cleaned.replace(/[.,]/g, "");
    } else {
      // Replace all separators before the last one
      cleaned =
        cleaned.substring(0, lastSep).replace(/[.,]/g, "") +
        "." +
        cleaned.substring(lastSep + 1);
    }
  }

  // Handle possible leading +/-
  let sign = 1;
  if (cleaned[0] === "-") {
    sign = -1;
    cleaned = cleaned.substring(1);
  } else if (cleaned[0] === "+") {
    cleaned = cleaned.substring(1);
  }

  const value = sign * Number(cleaned);

  if (isNaN(value)) {
    return { error: true };
  }

  if (value === 0) {
    return { value: undefined };
  }

  return {
    value,
    userFormatted: value.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  };
}

function isValidCurrency(code: string): boolean {
  try {
    new Intl.NumberFormat("en", { style: "currency", currency: code });
    return true;
  } catch {
    return false;
  }
}
